// src/elements/hudLite.ts
import { Z } from "../utils/constants";
import { createNarrationMuteButton } from "./buttons";
import { playMostVisibleNarration } from "./audio";
import { AudioController } from "@/utils/audioController";
import { syncNarrationHud, toggleNarrationMute } from "../utils/hudState";
import { enforceBackgroundAudioMix } from "../utils/mediaUtils";
import {
  clearAllSectionNarrationFlags,
  clearSectionNarrationFlags,
  playSectionAudio,
} from "../utils/narrationUtils";

// selector shared across the codebase
const NARRATION_SELECTOR =
  'audio[data-role="narration"], audio[data-narration], audio.narration, audio[data-section-audio="true"]';

// Local audio shims bridging HUD code to the global AudioController.
// These keep existing call sites intact while centralizing the logic.
const isAudioMuted = () => AudioController.isMuted();

const isAudioPlayable = () => AudioController.isAudioPlayable();

function fullyEnableNarration() {
  // User has explicitly opted into audio via the HUD.
  AudioController.setAudioAllowed(true);
  AudioController.setMuted(false, "hud");
}

export function isLiteNarrationHud(el: Element | null | undefined): boolean {
  if (!el || !(el as HTMLElement).classList) return false;
  const h = el as HTMLElement;
  return h.classList.contains("narration-hud") || h.dataset.hudKind === "narration";
}

export type HudHandle = {
  root: HTMLElement;
  show: () => void;
  showPersistent: () => void;
  hide: () => void;
  destroy: () => void;
  setHudVisibility: () => void;
};

export function hasNarration(section: HTMLElement): boolean {
  const attr = section.getAttribute("data-has-narration");
  if (attr !== null) {
    const v = attr.trim().toLowerCase();
    return v === "" || v === "1" || v === "true" || v === "yes";
  }
  return !!section.querySelector(NARRATION_SELECTOR);
}

function getNarrationAudio(section: HTMLElement): HTMLAudioElement | null {
  return section.querySelector<HTMLAudioElement>(NARRATION_SELECTOR);
}

async function setNarrationMuted(section: HTMLElement, muted: boolean) {
  const audio = getNarrationAudio(section);

  // Keep hudState/local pref in sync
  try { toggleNarrationMute(section, muted); } catch {}

  if (!audio) {
    // No audio element? Just sync HUD and bail.
    section.setAttribute("data-narration-muted", muted ? "1" : "0");
    syncNarrationHud(section);
    return;
  }

  if (muted) {
    // Hard-stop narration
    try { audio.pause(); } catch {}
    audio.muted = true;
    section.setAttribute("data-user-muted", "1");
    section.removeAttribute("data-user-unmuted");
    section.setAttribute("data-narration-muted", "1");
    document.dispatchEvent(new CustomEvent("sc:narration-mutechange", {
      detail: { muted: true, section, audio }
    }));
    syncNarrationHud(section);
    return;
  }

  // Unmute + try to actually start audio in the same gesture

  fullyEnableNarration();

  // One-time global sync on explicit unmute: clear stale per-section mute flags
  try { clearAllSectionNarrationFlags(); } catch {}

  clearSectionNarrationFlags(section);
  syncNarrationHud(section);

  audio.muted = false;
  if (audio.volume === 0) audio.volume = 1;

  // 🔊 Immediately try to play the most visible narration after unmuting
  if (isAudioPlayable()) {
    try {
      // Triggers narration for current section (if visible)
      playMostVisibleNarration(true);
    } catch (err) {
      console.warn("[HUD] Failed to auto-play narration after unmute", err);
    }
  }

  // Decide resume vs. fresh start precisely
  const shouldResume = audio.paused && audio.currentTime > 0 && !audio.ended;
  const shouldStartFresh = audio.currentTime === 0 && !audio.ended;
  try {
    if (audio.ended) {
      if (!audio.loop) {
        console.debug("[HUD] Audio ended; restarting on unmute for continuity");
        await playSectionAudio(audio, true); // force a clean restart
        return;
      } else {
        await playSectionAudio(audio, true); // restart looped section
      }
    } else if (shouldResume) {
      await audio.play(); // resume from current position
    } else if (shouldStartFresh) {
      await playSectionAudio(audio, true); // start from beginning (forced by user gesture)
    }

    section.setAttribute("data-audio-started", "1");

    document.dispatchEvent(new CustomEvent("sc:narration-mutechange", {
      detail: { muted: false, section, audio }
    }));
    try { enforceBackgroundAudioMix(section); } catch {}
  } catch {
    console.warn("[HUD] Playback resume/start failed after unmute — likely gesture restriction");
    // Browser rejected playback without a real gesture → reflect reality
    audio.muted = true;
    section.setAttribute("data-narration-muted", "1");
    try { (window as any).__storycore_narration_muted = true; } catch {}
  }
  syncNarrationHud(section);
}

/** Super lightweight HUD: only narration mute/unmute in top-right */
export function attachLiteHUD(section: HTMLElement): HudHandle {
  let controls = section.querySelector(".lite-hud-controls") as HTMLElement | null;
  if (!controls) {
    controls = document.createElement("div");
    controls.className =
      "lite-hud-controls absolute top-2 right-2 flex flex-col items-end gap-2";
    controls.style.zIndex = String(Z.HUD);
    section.appendChild(controls);
  }

  controls.dataset.hud = "lite";
  controls.dataset.hudKind = controls.dataset.hudKind || "basic";

  const hasNarr = hasNarration(section);
  let audio: HTMLAudioElement | null = null;

  if (hasNarr) {
    audio = getNarrationAudio(section);

    if (!controls.querySelector(".narration-mute-button")) {
      const narrationBtn = createNarrationMuteButton(isAudioMuted()) as HTMLButtonElement;

      narrationBtn.addEventListener("click", async (e) => {
        e.stopPropagation();
        const currentMuted =
          section.getAttribute("data-narration-muted") === "1" ||
          (audio ? (audio.muted || audio.paused) : isAudioMuted());

        await setNarrationMuted(section, !currentMuted);
      });

      narrationBtn.classList.add("narration-mute-button", "hud-btn");
      controls.appendChild(narrationBtn);
    }

    controls.classList.add("narration-hud");
    controls.dataset.hudKind = "narration";

    // Initial reconcile: trust global mute state (avoids flicker on mount)
    if (audio) {
      const startMuted = isAudioMuted();
      audio.muted = startMuted;
      if (startMuted) section.setAttribute("data-narration-muted", "1");
      else section.removeAttribute("data-narration-muted");
      // keep HUD in sync with real media state as it changes
      const sync = () => {
        const m = audio!.muted || audio!.paused || audio!.volume === 0;
        section.setAttribute("data-narration-muted", m ? "1" : "0");
        syncNarrationHud(section);
      };
      audio.addEventListener("play", sync);
      audio.addEventListener("pause", sync);
      audio.addEventListener("volumechange", sync);

      // Store for cleanup
      (controls as any).__narrSync = sync;
      (controls as any).__narrEl = audio;
    }

    // Sync button UI with state on mount
    syncNarrationHud(section);
  } else {
    controls.querySelectorAll(".narration-mute-button").forEach((el) => el.remove());
    controls.classList.remove("narration-hud");
    controls.dataset.hudKind = "basic";
  }

  const show = () => {
    controls.style.opacity = "1";
    controls.style.pointerEvents = "auto";
  };
  const showPersistent = show;
  const hide = () => {
    controls.style.opacity = "0.5";
  };
  const destroy = () => {
    // remove audio listeners if we attached them
    const sync = (controls as any).__narrSync as ((...a:any[])=>void) | undefined;
    const aEl = (controls as any).__narrEl as HTMLAudioElement | undefined;
    if (sync && aEl) {
      try {
        aEl.removeEventListener("play", sync);
        aEl.removeEventListener("pause", sync);
        aEl.removeEventListener("volumechange", sync);
      } catch {}
    }
    controls.remove();
  };
  const setHudVisibility = () => {};

  return { root: controls, show, showPersistent, hide, destroy, setHudVisibility };
}

// Backward-compat export
export { attachLiteHUD as attachRegularHUD };
